import { useQuery } from '@apollo/client';
import { AutocompleteController } from '@components/controllers/AutocompleteController';
import DeviceNameSpan from '@components/deviceNameSpan';
import { ALLOWED_OPERATIONAL_STATES } from '@features/reports/utils/allowedOperationalStates';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { GridOn, PictureAsPdf } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Paper,
  Popover,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, DefaultValues, FormState, SubmitHandler, useForm } from 'react-hook-form';
import {
  MetricField,
  TelemetryQuery,
  TriggerDataExtractionMutationVariables,
} from 'src/__generated__/graphql';
import FormAutocompleteArr from 'src/components/form/FormAutocompleteArr';
import { FormDateTimeRangePicker } from 'src/components/form/FormDateTimeRangePicker';
import { RangeTimeValue } from 'src/features/standardDesign/components/DateTimeRangePicker';
import { useAuth } from 'src/hooks';
import { z } from 'zod';
import GET_DEVICES_AVAILABLE_FIELDS from '../../graphql/queries/getDevicesAvailableFields';
import { getMeanWindowOptions } from '../../utils/meanWindowOptions';
import { getMetricFieldName, getMetricUnits } from '../../utils/metricHelpers';
import { getAvailableTimezonesSorted, getBrowserTimezone } from '../../utils/timezone';
import CreatedReportNotification from '../CreatedReportNotification';
import { DeviceSelectDialog } from '../selectables/DeviceSelectDialog';
import { MetricsUnitSelector } from '../selectables/MetricUnitSelector';

const validMetricFields: MetricField[] = [
  MetricField.AccumulatedVolume,
  MetricField.Angle,
  MetricField.Conductivity,
  MetricField.DissolvedOxygen,
  MetricField.DissolvedOxygenSaturation,
  MetricField.Flow,
  MetricField.Level,
  MetricField.LevelRaw,
  MetricField.OriginalDistance,
  MetricField.Orp,
  MetricField.Ph,
  MetricField.Position,
  MetricField.SnowLevel,
  MetricField.SnowLevelNonCalibrated,
  MetricField.SnowRadiation,
  MetricField.SnowTemperature,
  MetricField.SnowWaterEquivalent,
  MetricField.SnowWaterEquivalentNonCalibrated,
  MetricField.SnowWeight,
  MetricField.Temperature,
  MetricField.TurbidityFnu,
  MetricField.TurbidityNtu,
  MetricField.Velocity,
  MetricField.VolumeDay,
  MetricField.VolumeHour,
  MetricField.VolumeMonth,
];

interface IFormReportCreation {
  date: RangeTimeValue;
  meanWindow: string | null;
  timezone: string;
  devices: string[];
  metrics: { field: MetricField; unit: string; use: boolean }[];
  createPdf: boolean;
  createExcel: boolean;
  emails: string[];
}

interface IFormSubmitReportCreation {
  date: RangeTimeValue;
  meanWindow: string | null;
  timezone: string;
  request: TriggerDataExtractionMutationVariables['request'];
  createPdf: boolean;
  createExcel: boolean;
  emails: string[];
}

const formSchema = z
  .object({
    date: z
      .object({
        start: z.string(),
        stop: z.string().nullable(),
      })
      .refine(
        (data) => {
          if (data.stop === null) {
            return true;
          }
          return new Date(data.start) < new Date(data.stop);
        },
        {
          message: 'La fecha de inicio debe ser antes que la fecha de término',
        },
      ),
    timezone: z.string(),
    meanWindow: z.string().nullable(),
    devices: z.array(z.string()).min(1, 'Debe seleccionar al menos un dispositivo'),
    metrics: z
      .array(
        z.object({
          field: z.string(),
          unit: z.string(),
          use: z.boolean(),
        }),
      )
      .refine((data) => data.some((metric) => metric.use === true), {
        message: 'Debe seleccionar al menos un dispositivo',
      }),
    createExcel: z.boolean(),
    createPdf: z.boolean(),
    emails: z
      .array(z.string().email())
      .min(1, 'Tiene que haber al menos un email seleccionado para enviar el reporte'),
  })
  .refine((data) => data.createExcel || data.createPdf, {
    message: 'Tiene que elegir al menos un tipo de reporte',
  });

export type ReportCreationAttributes = {
  meanWindow?: string | null;
  timezone?: string;
  relativeDate?: string;
  startDate?: number;
  stopDate?: number;
  devices?: string[];
  metrics?: {
    metricName: MetricField;
    unit: string;
  }[];
  emails?: string[];
  createPdf?: boolean;
  createExcel?: boolean;
};

const FormFirstStepReportCreation = ({
  children,
  initialValues,
}: {
  children: {
    (props: {
      formState: FormState<IFormReportCreation>;
      submit: () => void;
      submitResult: IFormSubmitReportCreation | undefined;
    }): JSX.Element;
  };
  initialValues?: ReportCreationAttributes;
}) => {
  const { user } = useAuth();
  const { _, i18n } = useLingui();
  const currentTimezone = getBrowserTimezone();
  const allTimezones = getAvailableTimezonesSorted(currentTimezone);
  const initialTimezone = allTimezones.includes(currentTimezone) ? currentTimezone : 'UTC';
  const [openDeviceDialog, setOpenDeviceDialog] = useState(false);
  const [submitResult, setSubmitResult] = useState<IFormSubmitReportCreation>();

  const hardcodedMetricOrder = [
    MetricField.Angle,
    MetricField.Flow,
    MetricField.Level,
    MetricField.OriginalDistance,
    MetricField.AccumulatedVolume,
    MetricField.Velocity,
    MetricField.VolumeDay,
    MetricField.VolumeHour,
    MetricField.VolumeMonth,
    MetricField.AccumulatedVolume,
  ].sort((a, b) => getMetricFieldName(i18n, a).localeCompare(getMetricFieldName(i18n, b)));

  const sortMetrics = (a: { field: MetricField }, b: { field: MetricField }) => {
    if (hardcodedMetricOrder.includes(a.field) && !hardcodedMetricOrder.includes(b.field)) {
      return -1;
    }
    if (!hardcodedMetricOrder.includes(a.field) && hardcodedMetricOrder.includes(b.field)) {
      return 1;
    }
    if (hardcodedMetricOrder.includes(a.field) && hardcodedMetricOrder.includes(b.field)) {
      return hardcodedMetricOrder.indexOf(a.field) - hardcodedMetricOrder.indexOf(b.field);
    }
    return getMetricFieldName(i18n, a.field).localeCompare(getMetricFieldName(i18n, b.field));
  };

  const meanWindowOptions = getMeanWindowOptions(i18n);

  const defaultValues: DefaultValues<IFormReportCreation> = {
    date: { start: '-1d', stop: null },
    timezone: initialTimezone,
    meanWindow: meanWindowOptions[6].window,
    devices: [],
    metrics: [],
    createPdf: true,
    createExcel: true,
    emails: user?.email ? [user.email] : [],
  };

  const emailOptions = [];
  if (user) {
    emailOptions.push(user.email);
  }

  if (initialValues != null) {
    if (initialValues.meanWindow !== undefined) {
      defaultValues.meanWindow = initialValues.meanWindow;
    }
    if (initialValues.timezone !== undefined) {
      defaultValues.timezone = initialValues.timezone;
    }
    if (initialValues.relativeDate != null) {
      defaultValues.date = { start: initialValues.relativeDate, stop: null };
    } else if (initialValues.startDate != null) {
      defaultValues.date = {
        start: new Date(initialValues.startDate).toISOString(),
        stop: initialValues.stopDate ? new Date(initialValues.stopDate).toISOString() : null,
      };
    }

    if (initialValues.devices) {
      defaultValues.devices = initialValues.devices;
    }

    if (initialValues.metrics) {
      defaultValues.metrics = initialValues.metrics?.map((i) => ({
        field: i.metricName,
        unit: i.unit,
        use: true,
      }));
    }

    if (initialValues.emails) {
      defaultValues.emails = initialValues.emails;
    }

    if (initialValues.createPdf != null) {
      defaultValues.createPdf = initialValues.createPdf;
    }

    if (initialValues.createExcel != null) {
      defaultValues.createExcel = initialValues.createExcel;
    }
  }

  const { control, setValue, watch, formState, handleSubmit } = useForm<IFormReportCreation>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  const selectedDeviceIds = watch('devices');

  const { data: dataFields, loading } = useQuery(GET_DEVICES_AVAILABLE_FIELDS, {
    variables: { input: { ids: selectedDeviceIds, operationalStates: ALLOWED_OPERATIONAL_STATES } },
    skip: selectedDeviceIds.length === 0,
  });

  useEffect(() => {
    const fieldsByDevice = dataFields?.devices.map((i) => i.metrics.availableFields) ?? [];
    const fieldsSet = new Set<MetricField>();
    fieldsByDevice.forEach((fields) => {
      fields.forEach((aField) => {
        fieldsSet.add(aField);
      });
    });
    const metricFields = [...fieldsSet];
    const metricFieldsFiltered = metricFields.filter((field) => validMetricFields.includes(field));
    const newMetricControlValue = metricFieldsFiltered.map((field) => {
      const res = {
        field,
        unit: getMetricUnits(field)[0]?.value,
        use: true,
      };
      if (initialValues != null) {
        const metric = initialValues.metrics?.find((i) => i.metricName === field);
        if (metric != null) {
          res.unit = metric.unit;
          res.use = true;
        } else {
          res.use = false;
        }
      }
      return res;
    });
    setValue('metrics', newMetricControlValue.sort(sortMetrics));
  }, [dataFields]);

  const onSubmit: SubmitHandler<IFormReportCreation> = (data) => {
    const metricsToUse = data.metrics
      .filter((metric) => metric.use)
      .map((metric) => ({
        name: metric.field,
        unit: metric.unit,
      }));

    const request = {
      deviceIds: data.devices,
      metrics: metricsToUse,
    };

    setSubmitResult({
      request: request,
      date: data.date,
      meanWindow: data.meanWindow ?? null,
      timezone: data.timezone,
      createPdf: data.createPdf,
      createExcel: data.createExcel,
      emails: data.emails,
    });
  };
  return (
    <Box>
      <Box>
        <CreatedReportNotification />
        <Controller
          control={control}
          name="devices"
          render={({ field }) => (
            <DeviceSelectDialog
              value={field.value}
              open={openDeviceDialog}
              onClose={() => setOpenDeviceDialog(false)}
              onConfirm={(devices) => setValue('devices', devices)}
            />
          )}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 2 }}>
            <Typography variant="h6">
              <Trans>Configuración del Reporte</Trans>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormDateTimeRangePicker control={control} name="date" fullWidth />
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <Controller
                  control={control}
                  name="meanWindow"
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      value={
                        meanWindowOptions.find((option) => field.value === option.window) ?? null
                      }
                      onChange={(_, value) => field.onChange(value?.window ?? null)}
                      options={meanWindowOptions}
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={_(msg`Promediar datos por período`)}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={2}>
                <FormAutocompleteArr
                  control={control}
                  size="small"
                  name="timezone"
                  options={allTimezones}
                  label={_(msg`Zona Horaria`)}
                  disableClearable
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography mr={2}>
                    <Trans>Crear reporte en formato(s):</Trans>
                  </Typography>
                  <PictureAsPdf />
                  <Typography ml={1}>PDF</Typography>
                  <Controller
                    name="createPdf"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox
                        sx={{ mr: 2 }}
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    )}
                  />
                  <GridOn />
                  <Typography ml={1}>Excel</Typography>
                  <Controller
                    name="createExcel"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox
                        sx={{ mr: 2 }}
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    )}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography sx={{ whiteSpace: 'nowrap' }} mb={1}>
                    <Trans>Escriba los emails a enviar el reporte</Trans>:
                  </Typography>

                  <Box sx={{ flex: 1 }}>
                    <FormAutocompleteArr
                      size="small"
                      fullWidth
                      multiple
                      freeSolo
                      label="Emails"
                      options={emailOptions}
                      control={control}
                      name="emails"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Typography variant="h6">
            <Trans>Datos a Extraer de los Dispositivos</Trans>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              {loading ? (
                <Skeleton variant="rounded" width={'100%'} height={250} />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    gap: 2,
                    padding: 2,
                    maxHeight: '60vh',
                  }}
                >
                  <Typography variant="h6">
                    <Trans>Dispositivos</Trans>
                  </Typography>
                  <Divider />
                  <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    {selectedDeviceIds.length === 0 && (
                      <Typography>
                        <Trans>No hay dispositivos seleccionados</Trans>
                      </Typography>
                    )}
                    {selectedDeviceIds.map((id) => (
                      <DeviceItemDetail
                        key={id}
                        deviceId={id}
                        devicesMetricsInfo={dataFields?.devices}
                      />
                    ))}
                  </Box>
                  <Button variant="outlined" onClick={() => setOpenDeviceDialog(true)}>
                    <Trans>Seleccionar Dispositivos</Trans>
                  </Button>
                </Paper>
              )}
            </Grid>

            <Grid item xs={12} md={8}>
              {loading ? (
                <Skeleton variant="rounded" width={'100%'} height={250} />
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    gap: 2,
                    padding: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'flex-start',
                  }}
                >
                  <Typography variant="h6">
                    <Trans>Métricas</Trans>
                  </Typography>
                  <Divider />
                  {selectedDeviceIds.length === 0 ? (
                    <Typography>
                      <Trans>Seleccione un dispositivo para ver sus métricas disponibles.</Trans>
                    </Typography>
                  ) : (
                    <MetricsUnitSelector control={control} />
                  )}
                </Paper>
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>

      {typeof children === 'function' &&
        children({ formState, submit: handleSubmit(onSubmit), submitResult })}
    </Box>
  );
};

const DeviceItemDetail = (props: {
  deviceId: string;
  devicesMetricsInfo: TelemetryQuery['devices'] | undefined;
}) => {
  const { deviceId, devicesMetricsInfo } = props;
  const { i18n } = useLingui();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const metrics =
    devicesMetricsInfo
      ?.find((i) => i.id === deviceId)
      ?.metrics.availableFields.filter((field) => validMetricFields.includes(field))
      .map((field) => getMetricFieldName(i18n, field)) ?? [];

  return (
    <>
      <Typography onMouseEnter={handlePopoverOpen} onMouseLeave={handlePopoverClose}>
        <DeviceNameSpan deviceId={deviceId} />
      </Typography>
      <Popover
        sx={{ pointerEvents: 'none' }}
        slotProps={{ paper: { variant: 'outlined', elevation: 0 } }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ padding: 1 }}>
          {metrics.map((d) => (
            <Typography key={d}>{d}</Typography>
          ))}
        </Box>
      </Popover>
    </>
  );
};

export { FormFirstStepReportCreation };
export type { IFormReportCreation, IFormSubmitReportCreation };
